import { useEffect, useState } from "react";
import { api, BACKEND_URL } from "@/lib/api";
import { toast } from "sonner";
import { Heading } from "./NKDaily";
import { Plus, PencilSimple, Trash, X, ChartLineUp, UploadSimple } from "@phosphor-icons/react";

const resolveUrl = (u) => (u && u.startsWith("/api/uploads/")) ? `${BACKEND_URL}${u}` : u;

const PLACEMENT_LABELS = {
  home_top_banner: "Home — top banner",
  home_sticky_bottom: "Home — sticky bottom",
  lobby_top: "Daily/Ladder lobby — top",
};

export default function Ads() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try { const { data } = await api.get("/ads/admin"); setRows(data); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  useEffect(() => { load(); }, []);

  const save = async (ad) => {
    try {
      const payload = { ...ad };
      delete payload.ad_id; delete payload.impressions; delete payload.clicks; delete payload.created_at; delete payload.created_by; delete payload.updated_at;
      if (ad.ad_id) await api.put(`/ads/admin/${ad.ad_id}`, payload);
      else await api.post("/ads/admin", payload);
      toast.success("Saved");
      setEditing(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const del = async (ad) => {
    if (!confirm(`Delete "${ad.headline}"?`)) return;
    try { await api.delete(`/ads/admin/${ad.ad_id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-6">
      <header className="flex items-end justify-between gap-3">
        <Heading title="House Ads"
                 subtitle="In-house ads shown to free players. NK+ subscribers don't see ads. Each placement rotates randomly across all active ads." />
        <button onClick={() => setEditing({
          placement: "home_top_banner", headline: "", body: "", cta_label: "Learn more",
          cta_url: "", image_url: "", logo_url: "", status: "active", starts_at: null, ends_at: null, adsense_unit_id: "",
        })} data-testid="create-ad-btn"
                className="px-4 py-2 bg-nk-accent text-nk-bg text-sm font-medium rounded-sm inline-flex items-center gap-2 shrink-0">
          <Plus size={14} weight="bold" /> New ad
        </button>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {rows.map((a) => {
          const ctr = a.impressions ? ((a.clicks / a.impressions) * 100).toFixed(1) : "—";
          return (
            <div key={a.ad_id} className={`nk-card p-4 ${a.status === "paused" ? "opacity-60" : ""}`}>
              <div className="flex items-start justify-between gap-2 mb-2">
                <div>
                  <p className="eyebrow">{PLACEMENT_LABELS[a.placement] || a.placement}</p>
                  <p className="font-display text-lg">{a.headline}</p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-sm uppercase tracking-wider ${
                    a.status === "active" ? "bg-nk-accent/20 text-nk-accent" : "bg-nk-line/40 text-nk-mute"
                  }`}>{a.status}</span>
                  <button onClick={() => setEditing(a)} className="text-nk-mute hover:text-nk-text p-1"><PencilSimple size={14} /></button>
                  <button onClick={() => del(a)} className="text-nk-mute hover:text-nk-bad p-1"><Trash size={14} /></button>
                </div>
              </div>
              {a.body && <p className="text-xs text-nk-mute mb-2 line-clamp-2">{a.body}</p>}
              <div className="flex flex-wrap items-center gap-3 text-[11px]">
                <span className="text-nk-mute">CTA → <span className="text-nk-text font-mono">{a.cta_url}</span></span>
                <span className="ml-auto inline-flex items-center gap-1 text-nk-dim">
                  <ChartLineUp size={11} />
                  {a.impressions || 0} imp · {a.clicks || 0} clk · {ctr}% CTR
                </span>
              </div>
              {a.adsense_unit_id && (
                <p className="text-[10px] text-nk-gold mt-2 font-mono">AdSense unit: {a.adsense_unit_id}</p>
              )}
            </div>
          );
        })}
        {rows.length === 0 && (
          <p className="text-sm text-nk-dim col-span-full">No house ads yet. Click "New ad" to create one.</p>
        )}
      </div>

      {editing && <AdEditor initial={editing} onClose={() => setEditing(null)} onSave={save} />}
    </div>
  );
}

function AdEditor({ initial, onClose, onSave }) {
  const [a, setA] = useState(initial);
  const set = (k, v) => setA({ ...a, [k]: v });
  const placements = ["home_top_banner", "home_sticky_bottom", "lobby_top"];

  return (
    <div className="fixed inset-0 z-50 flex justify-end" onClick={onClose}>
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      <div className="relative w-full max-w-lg bg-nk-bg border-l border-nk-line p-6 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-xl font-medium">{a.ad_id ? "Edit ad" : "New ad"}</h2>
          <button onClick={onClose} className="text-nk-mute hover:text-nk-text"><X size={18} /></button>
        </div>
        <div className="space-y-3 text-sm">
          <L label="Placement">
            <select value={a.placement} onChange={(e) => set("placement", e.target.value)}
                    className="w-full bg-nk-surface border border-nk-line px-3 py-2 rounded-sm text-sm">
              {placements.map((p) => <option key={p} value={p}>{PLACEMENT_LABELS[p]}</option>)}
            </select>
          </L>
          <L label="Headline"><Inp v={a.headline} onC={(v) => set("headline", v)} maxLength={80} testId="ad-headline" /></L>
          <L label="Body (optional)"><Tarea v={a.body || ""} onC={(v) => set("body", v)} maxLength={240} /></L>
          <div className="grid grid-cols-2 gap-2">
            <L label="CTA label"><Inp v={a.cta_label} onC={(v) => set("cta_label", v)} maxLength={24} /></L>
            <L label="Status">
              <select value={a.status} onChange={(e) => set("status", e.target.value)}
                      className="w-full bg-nk-surface border border-nk-line px-3 py-2 rounded-sm text-sm">
                <option value="active">Active</option>
                <option value="paused">Paused</option>
              </select>
            </L>
          </div>
          <L label="Click-through URL"><Inp v={a.cta_url} onC={(v) => set("cta_url", v)} testId="ad-cta-url" /></L>
          <div className="grid grid-cols-2 gap-3">
            <L label="Main image (optional)"><FileUpload current={a.image_url} onUploaded={(url) => set("image_url", url || "")} /></L>
            <L label="Brand logo (optional)"><FileUpload current={a.logo_url} onUploaded={(url) => set("logo_url", url || "")} /></L>
          </div>
          <p className="text-[11px] text-nk-dim -mt-1">Logo shows as a small chip beside the headline (or large center if no main image).</p>
          <L label="Google AdSense unit ID (optional — overrides this slot when set)">
            <Inp v={a.adsense_unit_id || ""} onC={(v) => set("adsense_unit_id", v)} />
          </L>
          <div className="grid grid-cols-2 gap-2">
            <L label="Starts at (ISO, optional)"><Inp v={a.starts_at || ""} onC={(v) => set("starts_at", v || null)} placeholder="2026-06-10T00:00:00Z" /></L>
            <L label="Ends at (ISO, optional)"><Inp v={a.ends_at || ""} onC={(v) => set("ends_at", v || null)} placeholder="2026-06-30T23:59:59Z" /></L>
          </div>
          <div className="flex gap-2 pt-2">
            <button onClick={() => onSave(a)} disabled={!a.headline || !a.cta_url}
                    data-testid="ad-save-btn"
                    className="flex-1 px-4 py-2 bg-nk-accent text-nk-bg text-sm font-medium rounded-sm disabled:opacity-40">
              {a.ad_id ? "Save" : "Create"}
            </button>
            <button onClick={onClose} className="px-4 py-2 border border-nk-line text-sm text-nk-mute rounded-sm">Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function L({ label, children }) {
  return <div><label className="text-[11px] text-nk-mute uppercase tracking-wider">{label}</label><div className="mt-1">{children}</div></div>;
}
function Inp({ v, onC, maxLength, placeholder, testId }) {
  return <input value={v} onChange={(e) => onC(e.target.value)} maxLength={maxLength} placeholder={placeholder}
                data-testid={testId}
                className="w-full bg-nk-surface border border-nk-line px-3 py-2 rounded-sm text-sm focus:border-nk-accent outline-none" />;
}
function Tarea({ v, onC, maxLength }) {
  return <textarea value={v} onChange={(e) => onC(e.target.value)} maxLength={maxLength} rows={2}
                   className="w-full bg-nk-surface border border-nk-line px-3 py-2 rounded-sm text-sm focus:border-nk-accent outline-none" />;
}

function FileUpload({ current, onUploaded }) {
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Pick an image file"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onUploaded(data.url);
      toast.success("Uploaded");
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-2">
      {current && (
        <img src={resolveUrl(current)} alt="preview"
             className="w-12 h-12 object-contain border border-nk-line rounded-sm bg-nk-surface shrink-0" />
      )}
      <label className={`px-3 py-2 border border-nk-line rounded-sm text-xs cursor-pointer hover:border-nk-accent inline-flex items-center gap-2 ${busy ? "opacity-50" : ""}`}>
        <UploadSimple size={12} weight="bold" />
        {busy ? "Uploading…" : current ? "Replace" : "Upload"}
        <input type="file" accept="image/*" className="hidden" disabled={busy}
               onChange={(e) => upload(e.target.files?.[0])} />
      </label>
      {current && (
        <button type="button" onClick={() => onUploaded(null)} className="text-xs text-nk-mute hover:text-nk-bad">Remove</button>
      )}
    </div>
  );
}
